import hotkeys from 'hotkeys-js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { color, hexToRgba, HsvaColor, hsvaToHslaString, rgbaToHex, validHex } from '../utils/color-convert';
import { Pointer, PointerProps } from './components/color-alpha/pointer';
import Interactive, { Interaction } from './components/interactive';

export interface IColor {
    color: string;
    value: number;
    left: string;
    top: string;
    x: number;
    id: string;
}

export interface AlphaProps extends React.HTMLAttributes<HTMLDivElement> {
    prefixCls?: string;
    /** 选择器的宽度，默认 316px */
    width?: React.CSSProperties['width'];
    /** 选择器的高度，默认 16px */
    height?: React.CSSProperties['height'];
    /** pointer 组件 */
    pointer?: ({ prefixCls, left }: PointerProps) => JSX.Element;
    radius?: React.CSSProperties['borderRadius'];
    background?: string;
    /** 背景属性 */
    bgProps?: React.HTMLAttributes<HTMLDivElement>;
    /** 小球属性 */
    innerProps?: React.HTMLAttributes<HTMLDivElement>;
    /** 水平方向或者垂直方向 */
    direction?: 'vertical' | 'horizontal';
    colors: IColor[];
    setColors: Function;
    active: string;
    setActive: Function;
}

export const BACKGROUND_IMG =
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMUlEQVQ4T2NkYGAQYcAP3uCTZhw1gGGYhAGBZIA/nYDCgBDAm9BGDWAAJyRCgLaBCAAgXwixzAS0pgAAAABJRU5ErkJggg==';

const GradientInteractive = React.forwardRef<HTMLDivElement, AlphaProps>((props, ref) => {
    const {
        prefixCls = 'c-color-alpha',
        className,
        background,
        bgProps = {},
        innerProps = {},
        radius = 2.5,
        width,
        height = 20,
        direction = 'horizontal',
        style,
        pointer,
        colors,
        setColors,
        active,
        setActive,
        ...other
    } = props;
    const activePointer = useRef<IColor | null>(null);
    const isDragging = useRef<boolean>(false);
    const gradient = (startColor, endColor, step = 100) => {
        // 将 hex 转换为rgb
        const sColor = hexToRgba(startColor),
            eColor = hexToRgba(endColor);

        // 计算R\G\B每一步的差值
        const rStep = (eColor.r - sColor.r) / step,
            gStep = (eColor.g - sColor.g) / step,
            bStep = (eColor.g - sColor.g) / step;

        const gradientColorArr: string[] = [];
        for (let i = 0; i < step; i++) {
            // 计算每一步的hex值
            gradientColorArr.push(
                rgbaToHex({
                    r: Math.round(rStep * i + sColor.r),
                    g: Math.round(gStep * i + sColor.g),
                    b: Math.round(bStep * i + sColor.b),
                    a: 1,
                }),
            );
        }
        return gradientColorArr;
    };
    const handleChange = useCallback(
        (offset: Interaction) => {
            const value = offset.x > offset.width ? offset.width : offset.x < 0 ? 0 : offset.x;
            if (!activePointer.current) {
                colors.forEach((v) => {
                    const min = offset.x - 12;
                    const max = offset.x + 12;
                    if (v.x >= min && v.x <= max) {
                        activePointer.current = { ...v };
                        setActive(v.id);
                    }
                });
                if (!activePointer.current && !isDragging.current) {
                    let start: null | IColor = null;
                    let end: null | IColor = null;
                    // 筛选出最近的两个
                    colors.forEach((color) => {
                        if (color.value < offset.left) {
                            if (start) {
                                start = start.value > color.value ? start : color;
                            } else {
                                start = color;
                            }
                        } else {
                            if (end) {
                                end = end.value < color.value ? end : color;
                            } else {
                                end = color;
                            }
                        }
                    });
                    start = start || {
                        color: '#ffffff',
                        x: 0,
                        value: 0,
                        left: '0%',
                        top: '0%',
                        id: uuidv4(),
                    };
                    end = end || {
                        color: '#ffffff',
                        x: Number(width),
                        value: Number(width),
                        left: '100%',
                        top: '0%',
                        id: uuidv4(),
                    };
                    let colorList = gradient(start.color, end.color, 100);
                    // TODO:间距太近有时候会计算出错
                    const c = colorList[Math.round(((offset.left - start.value) / (end.value - start.value)) * 100)];

                    activePointer.current = {
                        color: validHex(c) ? c : start.color,
                        x: value,
                        value: offset.left,
                        left: offset.left * 100 + '%',
                        top: '',
                        id: uuidv4(),
                    };
                    setColors([...colors, activePointer.current]);
                    setActive(activePointer.current.id);
                }
                return;
            } else if (isDragging.current) {
                setColors((list) => {
                    const index = list.findIndex((v) => v.id === activePointer.current?.id);
                    list[index] = {
                        ...list[index],
                        x: value,
                        value: offset.left,
                        left: offset.left * 100 + '%',
                    };
                    activePointer.current = list[index];
                    return [...list];
                });
            }
        },
        [colors, activePointer.current, isDragging.current],
    );
    useEffect(() => {
        const handler = (e: any) => {
            const { key } = e;
            if (['Delete', 'Backspace'].includes(key)) {
                if (colors.length > 2) {
                    const index = colors.findIndex((v) => v.id === activePointer.current?.id);
                    colors.splice(index, 1);
                    activePointer.current = colors[0];
                    setColors([...colors]);
                    setActive(colors[0].id);
                }
                return;
            }
        };
        if (!activePointer.current && colors.length) {
            activePointer.current = colors[0];
            setActive(colors[0].id);
        }

        document.addEventListener('keydown', handler);
        return () => {
            document.removeEventListener('keydown', handler);
        };
    }, [colors]);
    let backgroundImage = '';
    colors
        ?.sort((a, b) => a.value - b.value)
        .forEach((item, index) => {
            const { color, left } = item;
            backgroundImage += `${color} ${left}${index === colors.length - 1 ? '' : ','}`;
        });
    return (
        <div
            {...other}
            className={[prefixCls, `${prefixCls}-${direction}`, className || ''].filter(Boolean).join(' ')}
            style={{
                borderRadius: radius,
                // background: `url(${BACKGROUND_IMG}) left center`,
                backgroundColor: '#fff',
                ...style,
                position: 'relative',
                ...{ width, height: (height as number) + 2 },
                border: '1px solid rgba(0, 0, 0, 0.05)',
            }}
            ref={ref}
        >
            <div
                {...bgProps}
                style={{
                    inset: 0,
                    position: 'absolute',
                    background: background || '',
                    borderRadius: radius,
                    ...bgProps.style,
                }}
            />
            <Interactive
                {...innerProps}
                style={{
                    ...innerProps.style,
                    inset: 0,
                    zIndex: 1,
                    position: 'absolute',
                    borderRadius: '2px',
                    backgroundImage: `linear-gradient(90deg, ${backgroundImage})`,
                }}
                onMove={(offset: Interaction) => {
                    isDragging.current = true;
                    handleChange(offset);
                }}
                onDown={(offset: Interaction) => {
                    activePointer.current = null;
                    isDragging.current = false;
                    handleChange(offset);
                }}
            >
                {colors &&
                    colors.map((v) => {
                        return React.createElement(pointer || Pointer, {
                            prefixCls,
                            ...v,
                            active: active === v.id,
                            activePointer: activePointer,
                        });
                    })}
            </Interactive>
        </div>
    );
});

GradientInteractive.displayName = 'Aplha';

export default GradientInteractive;
